import { getLocales } from 'expo-localization';
import { logger } from 'features/observability/logger';

import { useDragonBallExperiment } from 'experiments/use-dragon-ball-experiment';
import { getEarlyIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getAllFlags } from 'state/launchdarkly/platform-client';
import { AppTakeoverLDFlag } from 'state/launchdarkly/variations';
import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { appTakeoverRegistry } from '../registry.generated';
import { AppTakeoverConfig, TakeoverMenuSectionConfig, TakeoverPdpItemConfig } from '../types';

export type AppTakeoverType = AppTakeoverConfig;

export type GetAppTakeoverResult = Partial<
  AppTakeoverConfig & { isTakeoverActive: boolean; name: string }
>;

const INACTIVE = Object.freeze({ isTakeoverActive: false as const });
const DRAGON_BALL_TAKEOVER_NAME = 'dragonball';
let lastReportedScheduleCollision: string | null = null;

const getRegisteredTakeover = (name: string) =>
  Object.prototype.hasOwnProperty.call(appTakeoverRegistry, name)
    ? appTakeoverRegistry[name]
    : undefined;

/** Get a takeover if one is active per LD date ranges. */
export const useAppTakeover = (): GetAppTakeoverResult => {
  const appTakeover = useFlag(LaunchDarklyFlag.APP_TAKEOVER);
  return isAppTakeoverEnabledCore(appTakeover as AppTakeoverLDFlag);
};

/** Get an active takeover after applying experiment visibility gates. */
export const useExperimentGatedAppTakeover = (): GetAppTakeoverResult => {
  const appTakeover = useAppTakeover();
  const isDragonBallTreatment = useDragonBallExperiment(true);

  if (appTakeover.name === DRAGON_BALL_TAKEOVER_NAME && !isDragonBallTreatment) {
    return INACTIVE;
  }

  return appTakeover;
};

/**
 * Raw Dragon Ball targeting data for exposure eligibility. This intentionally
 * bypasses the treatment visibility gate so control and treatment use the same
 * exposure conditions.
 */
export const useDragonBallTakeoverExposureEligibility = () => {
  const appTakeover = useAppTakeover();
  const isActive = appTakeover.name === DRAGON_BALL_TAKEOVER_NAME;

  return {
    isActive,
    targetSectionIds: isActive ? appTakeover.menu?.targetSectionIds : undefined,
    targetMenuItemIds: isActive ? appTakeover.pdp?.targetMenuItemIds : undefined,
  };
};

/** OUTSIDE OF LD PROVIDER ONLY - get a takeover if one is active per LD date ranges */
export const getAppTakeover = () => {
  const appTakeover = getAllFlags()?.[LaunchDarklyFlag.APP_TAKEOVER] as AppTakeoverLDFlag;
  return isAppTakeoverEnabledCore(appTakeover as AppTakeoverLDFlag);
};

const isAppTakeoverEnabledCore = (appTakeover: AppTakeoverLDFlag): GetAppTakeoverResult => {
  if (getEarlyIsDesktopWebBreakpoint()) {
    // disable on desktop, designs assume mobile as is most of our users
    return INACTIVE;
  }

  if (getIsDisabledLanguage()) {
    return INACTIVE;
  }

  if (!appTakeover || !appTakeover?.length) {
    lastReportedScheduleCollision = null;
    return INACTIVE;
  }

  const now = Date.now();
  const activeTakeovers = appTakeover.filter(takeover => {
    const start = Date.parse(takeover.startDate);
    const end = Date.parse(takeover.endDate);
    if (
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start > end ||
      now < start ||
      now > end
    ) {
      return false;
    }

    const registered = getRegisteredTakeover(takeover.name);
    return Boolean(
      registered && registered.regions.some(takeoverRegion => takeoverRegion === region)
    );
  });
  if (activeTakeovers.length > 1) {
    const collisionKey = activeTakeovers
      .map(({ name, startDate, endDate }) => `${name}|${startDate}|${endDate}`)
      .sort()
      .join('::');
    if (collisionKey !== lastReportedScheduleCollision) {
      logger.error({
        message: 'Multiple app takeover schedules are active; takeover theming is disabled.',
        activeTakeovers,
      });
      lastReportedScheduleCollision = collisionKey;
    }
    return INACTIVE;
  }

  lastReportedScheduleCollision = null;
  const activeTakeover = activeTakeovers[0];
  if (activeTakeover) {
    // Registry is generated from manifests and keyed by ldName. Unknown names
    // are excluded before collision handling and resolve to INACTIVE.
    const registered = getRegisteredTakeover(activeTakeover.name);
    if (registered) {
      return {
        ...registered.config,
        isTakeoverActive: true,
        name: activeTakeover.name,
      };
    }
  }
  return INACTIVE;
};

/** return a SHP config if takeover is active for the passed home page */
export const useAppTakeoverSimpleHomePage = (
  homePageId: string
): Partial<AppTakeoverType['homePage'] & { isTakeoverActive: boolean; fontFamily: string }> => {
  const appTakeover = useAppTakeover();

  if (!homePageId) {
    return { isTakeoverActive: false };
  }

  if (appTakeover?.homePage?.targetSimpleHomePageIds?.includes(homePageId)) {
    return {
      ...appTakeover?.homePage,
      isTakeoverActive: true,
      fontFamily: appTakeover?.fontFamily,
    };
  }
  return INACTIVE;
};

type MenuSectionResult = TakeoverMenuSectionConfig & {
  isTakeoverActive: boolean;
};

/** returns a menu section config if takeover is active for the passed section */
export const useAppTakeoverMenuSection = (sectionId: string): MenuSectionResult => {
  const appTakeover = useExperimentGatedAppTakeover();

  if (!sectionId) {
    return INACTIVE;
  }

  const takeoverSectionConfig = appTakeover?.menu?.targetSectionIds[sectionId];
  if (takeoverSectionConfig && Object.keys(takeoverSectionConfig).length > 0) {
    return { ...takeoverSectionConfig, isTakeoverActive: true };
  }

  return INACTIVE;
};

export type PdpResult = Partial<
  AppTakeoverType['pdp'] & { isTakeoverActive: boolean; fontFamily: string } & TakeoverPdpItemConfig
>;

const getAppTakeoverPdpResult = (
  appTakeover: GetAppTakeoverResult,
  itemConfig?: TakeoverPdpItemConfig
): PdpResult => {
  if (!itemConfig) {
    return INACTIVE;
  }

  return {
    ...itemConfig,
    ...appTakeover?.pdp,
    isTakeoverActive: true,
    fontFamily: appTakeover?.fontFamily,
  };
};

export const getAppTakeoverPdpFromUrl = (
  appTakeover: GetAppTakeoverResult,
  itemUrl: string
): PdpResult => {
  if (!itemUrl) {
    return INACTIVE;
  }

  const itemConfig = Object.entries(appTakeover?.pdp?.targetMenuItemIds || {}).find(([id]) =>
    itemUrl.includes(id)
  )?.[1];

  return getAppTakeoverPdpResult(appTakeover, itemConfig);
};

export const useAppTakeoverCartItem = (itemUrl: string): PdpResult => {
  const appTakeover = useExperimentGatedAppTakeover();
  return getAppTakeoverPdpFromUrl(appTakeover, itemUrl);
};

/** returns a PDP config if takeover is active for the menu item */
export const useAppTakeoverPdp = (itemId: string): PdpResult => {
  const appTakeover = useExperimentGatedAppTakeover();

  if (!itemId) {
    return INACTIVE;
  }

  const itemConfig = appTakeover?.pdp?.targetMenuItemIds?.[itemId];
  return getAppTakeoverPdpResult(appTakeover, itemConfig);
};

const getIsDisabledLanguage = () => {
  if (region === Region.US) {
    return false;
  }

  try {
    const deviceLanguage = getLocales()?.[0]?.languageCode?.toLowerCase();
    if (deviceLanguage?.includes('fr')) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
};
